import React from 'react'
import Image, { StaticImageData } from 'next/image'
import useTranslation from 'next-translate/useTranslation'
import { Text } from 'components/Text'
import { SectionTitle } from '../../../../AllProducts/AllProducts.styles'
import {
  ProductCatalogRoot,
  ProductCta,
} from '../../../../AllProducts/components/ProductCatalog/ProductCatalog.styles'
import {
  PriceWrapper,
  HeaderWrapper,
  OriginalPrice,
  DiscountedPrice,
  ProductImage,
  ProductsGrid,
  ProductCard,
  ProductTitleWrapper,
  CtaWrapper,
} from './EssentialBox.styles'
import { Button } from 'components/Button'
import smallBoxImage from '../../../../../../../../public/assets/product/small-box.png'
import classicBoxImage from '../../../../../../../../public/assets/product/classic-box.png'
import bigBoxImage from '../../../../../../../../public/assets/product/big-box.png'
import Link from 'next/link'
import { Box } from 'components/Box'
import { FetchProducts } from 'features/ECommerce/api/queries/useFetchProducts'
import { calculateSafePrice } from 'utils/currency'

type BoxType = {
  title: string
  image: StaticImageData
  imageAlt: string
  originalPrice: string
  discountedPrice: string
  ctaText: string
  link: string
  name: string
}

type EssentialBoxProps = {
  data: FetchProducts.Response
  onAdd: (productId: string, quantity: number, productName: string) => void
}

export const EssentialBox = ({data, onAdd}: EssentialBoxProps) => {
  const { t } = useTranslation()

  const products = data.products

  const bigBox = products.find((product) => product.slug === 'big-box')
  const smallBox = products.find((product) => product.slug === 'small-box')
  const classicBox = products.find((product) => product.slug === 'classic-box')

  const boxes: BoxType[] = [
    {
      title: 'products:essentialBox.smallBox.title',
      image: smallBoxImage,
      imageAlt: 'products:essentialBox.smallBox.imageAlt',
      originalPrice: calculateSafePrice(smallBox?.price.subtotal),
      discountedPrice: calculateSafePrice(smallBox?.price.total),
      ctaText: 'products:essentialBox.cta',
      link: '/products/small-box',
      name: 'small-box',
    },
    {
      title: 'products:essentialBox.classicBox.title',
      image: classicBoxImage,
      imageAlt: 'products:essentialBox.classicBox.imageAlt',
      originalPrice: calculateSafePrice(classicBox?.price.subtotal),
      discountedPrice: calculateSafePrice(classicBox?.price.total),
      ctaText: 'products:essentialBox.cta',
      link: '/products/classic-box',
      name: 'classic-box',
    },
    {
      title: 'products:essentialBox.bigBox.title',
      image: bigBoxImage,
      imageAlt: 'products:essentialBox.bigBox.imageAlt',
      originalPrice: calculateSafePrice(bigBox?.price.subtotal),
      discountedPrice: calculateSafePrice(bigBox?.price.total),
      ctaText: 'products:essentialBox.cta',
      link: '/products/big-box',
      name: 'big-box',
    },
  ]

  const BoxItem = ({
    title,
    image,
    originalPrice,
    discountedPrice,
    ctaText,
    link,
    name,
    imageAlt
  }: BoxType) => (
    <ProductCard>
      <Box>
        <ProductTitleWrapper>
          <Text size='bodyExtraLarge' weight="medium" color="accent-black">
            {t(title)}
          </Text>
        </ProductTitleWrapper>
        <PriceWrapper>
          <OriginalPrice>{originalPrice}</OriginalPrice>
          <DiscountedPrice>{discountedPrice}</DiscountedPrice>
        </PriceWrapper>
      </Box>

      <CtaWrapper>
        <ProductImage className={name}>
          <Image objectPosition="bottom" src={image} alt={`${imageAlt}`} />
        </ProductImage>
        <ProductCta>
          <Link href={link}>
            <Button>{t(ctaText)}</Button>
          </Link>
        </ProductCta>
      </CtaWrapper>
    </ProductCard>
  )

  return (
    <ProductCatalogRoot>
      <HeaderWrapper>
        <SectionTitle align="center">{t('products:essentialBox.title')}</SectionTitle>
        <Text align="center" size={{'@initial': 'body', '@bp3': 'bodyLarge'}}  color="accent-black">
          {t('products:essentialBox.subtitle')}
        </Text>
      </HeaderWrapper>

      <ProductsGrid>
        {boxes.map((box, index) => (
          <BoxItem key={index} {...box} />
        ))}
      </ProductsGrid>
    </ProductCatalogRoot>
  )
}
